import React, { useState } from 'react';
import './ConceptExplainer.css';

const ConceptExplainer = ({ conceptId, onBack }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const concepts = {
    'what-is-miden': {
      title: 'What is Miden?',
      steps: [
        {
          title: 'A Privacy-First Blockchain',
          content: 'Miden is a blockchain that lets you do things privately. Unlike most blockchains where everyone can see your transactions, Miden keeps your business private.',
          visual: 'shield'
        },
        {
          title: 'Prove Without Revealing',
          content: 'You can prove you did something (like paid someone) without showing the amount, the recipient, or any other details. It\'s like showing a receipt that says "payment verified" without showing what you paid.',
          visual: 'proof'
        },
        {
          title: 'Your Device Does the Work',
          content: 'All the heavy computation happens on your device, not on the blockchain. This means faster transactions and lower costs.',
          visual: 'device'
        }
      ]
    },
    'how-proofs-work': {
      title: 'How Proofs Work',
      steps: [
        {
          title: 'Step 1: Run Locally',
          content: 'Your device runs the code locally. For example, if you\'re making a payment, your device calculates everything on your computer or phone.',
          visual: 'local'
        },
        {
          title: 'Step 2: Create a Proof',
          content: 'Your device creates a mathematical proof that you did the calculation correctly, without revealing the actual numbers or details.',
          visual: 'proof-create'
        },
        {
          title: 'Step 3: Verify on Chain',
          content: 'The blockchain verifies your proof instantly. It checks that your proof is valid without needing to see what you actually did.',
          visual: 'verify'
        }
      ]
    },
    'accounts-notes': {
      title: 'Accounts & Notes',
      steps: [
        {
          title: 'Private Accounts',
          content: 'Your account holds your assets privately. Only you know what\'s in your account, and you can prove you have enough without revealing the exact amount.',
          visual: 'account'
        },
        {
          title: 'Invisible Notes',
          content: 'Notes are like invisible envelopes that move value between accounts. They contain the transaction details, but only the sender and receiver can see them.',
          visual: 'note'
        },
        {
          title: 'Secure Transfers',
          content: 'When you send a note, it\'s encrypted and can only be opened by the intended recipient. The blockchain verifies the transfer happened without seeing the contents.',
          visual: 'transfer'
        }
      ]
    },
    'client-side-proving': {
      title: 'Client-Side Proving',
      steps: [
        {
          title: 'Everything Runs on Your Device',
          content: 'All computation happens on your computer or phone first. This means your data never leaves your device until it\'s been turned into a proof.',
          visual: 'device-compute'
        },
        {
          title: 'Only Proofs Go to Chain',
          content: 'Instead of sending all your transaction data, you only send a tiny proof. This proof is like a fingerprint that proves you did something correctly.',
          visual: 'proof-send'
        },
        {
          title: 'Privacy by Design',
          content: 'Because the blockchain only sees proofs, not your actual data, your privacy is protected. You can use the blockchain without exposing your information.',
          visual: 'privacy'
        }
      ]
    }
  };

  const concept = concepts[conceptId];
  if (!concept) {
    return (
      <div className="concept-explainer">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="concept-error">Concept not found</div>
      </div>
    );
  }

  const currentStepData = concept.steps[currentStep];
  const isLastStep = currentStep === concept.steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onBack();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderVisual = (visualType) => {
    switch (visualType) {
      case 'shield':
        return (
          <div className="visual-shield">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#f97316" strokeWidth="2"/>
            </svg>
          </div>
        );
      case 'proof':
        return (
          <div className="visual-proof">
            <div className="proof-box">Proof</div>
            <div className="proof-arrow">→</div>
            <div className="proof-check">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{display: 'inline-block', marginRight: '6px', verticalAlign: 'middle'}}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Verified
            </div>
          </div>
        );
      case 'device':
        return (
          <div className="visual-device">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="#f97316" strokeWidth="2"/>
              <line x1="8" y1="21" x2="16" y2="21" stroke="#f97316" strokeWidth="2"/>
            </svg>
          </div>
        );
      case 'local':
        return (
          <div className="visual-flow">
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                </svg>
              </div>
              <div className="flow-label">Your Device</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              </div>
              <div className="flow-label">Runs Code</div>
            </div>
          </div>
        );
      case 'proof-create':
        return (
          <div className="visual-flow">
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div className="flow-label">Creates Proof</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div className="flow-label">Hides Details</div>
            </div>
          </div>
        );
      case 'verify':
        return (
          <div className="visual-flow">
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </div>
              <div className="flow-label">Send Proof</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="flow-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className="flow-label">Chain Verifies</div>
            </div>
          </div>
        );
      case 'account':
        return (
          <div className="visual-account">
            <div className="account-box">
              <div className="account-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="account-label">Your Account</div>
              <div className="account-secret">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '6px', verticalAlign: 'middle'}}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Private
              </div>
            </div>
          </div>
        );
      case 'note':
        return (
          <div className="visual-note">
            <div className="note-envelope">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div className="note-label">Invisible Note</div>
            <div className="note-secret">Only sender & receiver can see</div>
          </div>
        );
      case 'transfer':
        return (
          <div className="visual-transfer">
            <div className="transfer-from">Account A</div>
            <div className="transfer-arrow">
              → 
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', margin: '0 4px', verticalAlign: 'middle'}}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              →
            </div>
            <div className="transfer-to">Account B</div>
          </div>
        );
      case 'device-compute':
        return (
          <div className="visual-compute">
            <div className="compute-device">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
              </svg>
              Your Device
            </div>
            <div className="compute-process">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
              </svg>
              Computing...
            </div>
            <div className="compute-result">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Proof Created
            </div>
          </div>
        );
      case 'proof-send':
        return (
          <div className="visual-send">
            <div className="send-proof">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              Tiny Proof
            </div>
            <div className="send-arrow">↓</div>
            <div className="send-chain">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
                <circle cx="5" cy="12" r="3"/>
                <circle cx="19" cy="12" r="3"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              Blockchain
            </div>
          </div>
        );
      case 'privacy':
        return (
          <div className="visual-privacy">
            <div className="privacy-shield">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="privacy-text">Your Data Stays Private</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="concept-explainer">
      <div className="concept-explainer-window">
        <div className="panel-header">
          <div className="terminal-dots">
            <span className="dot red"></span>
            <span className="dot yellow"></span>
            <span className="dot green"></span>
          </div>
          <button onClick={onBack} className="close-button">×</button>
        </div>
        
        <div className="explainer-content">
          <h1 className="explainer-title">{concept.title}</h1>
          
          <div className="step-indicator">
            <div className="step-dots">
              {concept.steps.map((_, index) => (
                <div
                  key={index}
                  className={`step-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => setCurrentStep(index)}
                />
              ))}
            </div>
            <div className="step-counter">
              Step {currentStep + 1} of {concept.steps.length}
            </div>
          </div>

          <div className="step-content-wrapper">
            <div className="step-visual">
              {renderVisual(currentStepData.visual)}
            </div>
            
            <div className="step-text">
              <h2 className="step-title">{currentStepData.title}</h2>
              <p className="step-description">{currentStepData.content}</p>
            </div>
          </div>

          <div className="step-navigation">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="nav-button prev-button"
            >
              ← Previous
            </button>
            <button
              onClick={handleNext}
              className="nav-button next-button"
            >
              {isLastStep ? 'Done' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptExplainer;

